import { STORAGE_KEYS } from './storageKeys';

/**
 * Derives the active portal key from the current browser URL path.
 * Matches are ordered longest-prefix-first so /visitor-admin doesn't
 * accidentally match /visitor or /visitor-admin matches before /admin.
 */
function getActivePortal() {
  const path = window.location.pathname;

  if (path.startsWith('/superadmin'))      return 'superadmin';
  if (path.startsWith('/exhibitor'))       return 'exhibitor';
  if (path.startsWith('/buyer-admin'))     return 'buyerAdmin';
  if (path.startsWith('/visitor-admin'))   return 'visitorAdmin';
  if (path.startsWith('/hotelmanagement')) return 'hotelManagement';
  if (path.startsWith('/cabmanagement'))   return 'cabManagement';
  if (path.startsWith('/hospitality'))     return 'hospitality';
  if (path.startsWith('/cab-service'))     return 'cabService';
  if (path.startsWith('/agent'))           return 'agent';
  if (path.startsWith('/associate'))       return 'associate';
  if (path.startsWith('/sponser'))         return 'sponser';
  if (path.startsWith('/adminvip'))        return 'adminVip';

  return null;
}

export const portalAuth = {
  /**
   * Returns the portal key for the current URL (e.g. 'superadmin').
   */
  getActivePortal,

  // ─── WRITE ──────────────────────────────────────────────────────────────────

  /**
   * Persist a full login session for a given portal.
   * Call this inside the login .then() handler instead of writing
   * to jff_token / permission / user_id individually.
   *
   * @param {string} portal      - portal key (e.g. 'superadmin')
   * @param {string} token       - JWT returned by the server
   * @param {Array}  permissions - permissions array from response.data.category.permissions
   * @param {string} userId      - response.data.category._id
   * @param {string} [typeOfUser] - optional type_of_user value
   * @param {string} [category]   - optional category value
   */
  setAuth(portal, token, permissions, userId, typeOfUser = '', category = '') {
    const keys = STORAGE_KEYS[portal];
    if (!keys) {
      console.warn(`portalAuth.setAuth: unknown portal "${portal}"`);
      return;
    }
    localStorage.setItem(keys.token,        token);
    localStorage.setItem(keys.permission,   JSON.stringify(permissions));
    localStorage.setItem(keys.user_id,      userId);
    localStorage.setItem(keys.type_of_user, typeOfUser);
    localStorage.setItem(keys.category,     category);
  },

  // ─── READ (portal-explicit) ─────────────────────────────────────────────────

  getToken(portal) {
    const keys = STORAGE_KEYS[portal];
    return keys ? localStorage.getItem(keys.token) : null;
  },

  getPermission(portal) {
    const keys = STORAGE_KEYS[portal];
    if (!keys) return null;
    const raw = localStorage.getItem(keys.permission);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  },

  getUserId(portal) {
    const keys = STORAGE_KEYS[portal];
    return keys ? localStorage.getItem(keys.user_id) : null;
  },

  getTypeOfUser(portal) {
    const keys = STORAGE_KEYS[portal];
    return keys ? localStorage.getItem(keys.type_of_user) : null;
  },

  getCategory(portal) {
    const keys = STORAGE_KEYS[portal];
    return keys ? localStorage.getItem(keys.category) : null;
  },

  isLoggedIn(portal) {
    return !!this.getToken(portal);
  },

  /**
   * Returns true if the current portal user has delete permission.
   * Only meaningful for the superadmin portal.
   */
  canDelete() {
    const portal = getActivePortal();
    if (!portal) return false;
    const keys = STORAGE_KEYS[portal];
    if (!keys || !keys.delete_permission) return false;
    return localStorage.getItem(keys.delete_permission) === 'true';
  },

  // ─── READ (URL-aware, drop-in for raw localStorage.getItem calls) ───────────

  /**
   * Reads the token for whichever portal owns the current URL.
   * Drop-in replacement for: portalAuth.getActiveToken()
   */
  getActiveToken() {
    const portal = getActivePortal();
    if (!portal) return null;
    return this.getToken(portal);
  },

  /**
   * Drop-in replacement for: portalAuth.getActivePermission()
   * Returns the raw JSON string (as the old code expected).
   */
  getActivePermission() {
    const portal = getActivePortal();
    if (!portal) return null;
    const keys = STORAGE_KEYS[portal];
    return keys ? localStorage.getItem(keys.permission) : null;
  },

  /**
   * Drop-in replacement for: portalAuth.getActiveUserId()
   */
  getActiveUserId() {
    const portal = getActivePortal();
    if (!portal) return null;
    return this.getUserId(portal);
  },

  /**
   * Drop-in replacement for: portalAuth.getActiveTypeOfUser()
   */
  getActiveTypeOfUser() {
    const portal = getActivePortal();
    if (!portal) return null;
    return this.getTypeOfUser(portal);
  },

  /**
   * Drop-in replacement for: portalAuth.getActiveCategory()
   */
  getActiveCategory() {
    const portal = getActivePortal();
    if (!portal) return null;
    return this.getCategory(portal);
  },

  // ─── DELETE ─────────────────────────────────────────────────────────────────

  /**
   * Clears all 5 scoped keys for a given portal (logout).
   * Call this instead of the 5 separate removeItem calls.
   */
  logout(portal) {
    const keys = STORAGE_KEYS[portal];
    if (!keys) {
      console.warn(`portalAuth.logout: unknown portal "${portal}"`);
      return;
    }
    localStorage.removeItem(keys.token);
    localStorage.removeItem(keys.permission);
    localStorage.removeItem(keys.user_id);
    localStorage.removeItem(keys.type_of_user);
    localStorage.removeItem(keys.category);
    if (keys.delete_permission) {
      localStorage.removeItem(keys.delete_permission);
    }
  },

  /**
   * Logout for whichever portal owns the current URL.
   */
  logoutActive() {
    const portal = getActivePortal();
    if (portal) this.logout(portal);
  },
};

export default portalAuth;
